// apps/web/src/features/russia-requests/components/editor/RussiaPositionEditRow.tsx
//
// One editable SIMPLE position card for the Russia editor
// (#1545). Fields mirror China's position layout; the photo grid reuses
// the China `DraftItemPhotos` strip unchanged so there is one photo
// implementation across both corridors.
//
// @aiInvariant Russia item ids are server UUIDs, not the China `it-NNNN`
//   shape, so `DraftItemPhotos`'s own persisted-id guard never authorizes
//   saved photos here. This row registers each issued photo key against
//   the shared `DraftPhotoPreviews` registry itself, then asks the
//   registry to resolve the signed download URL — same one-fetch-per-key
//   cache, no duplicated fetch logic.

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Icon, IconButton, Input, Textarea } from '../../../../ui/index.js';
import { DraftItemPhotos, useDraftPhotoPreviews } from '../../../requests/draft-media/index.js';
import { Field } from './EditorPrimitives.js';
import type { RussiaPositionFields, RussiaPositionRow } from './useRussiaDraftPositions.js';

const ISSUED_PHOTO_KEY_RE = /^request-item-photo\/[0-9a-f-]{36}\/[0-9a-f-]{36}$/i;

function parseIntOrNull(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isInteger(n) && n > 0 ? n : null;
}

function parseDecimalOrNull(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function numToText(value: number | null): string {
  return value === null ? '' : String(value);
}

export interface RussiaPositionEditRowProps {
  row: RussiaPositionRow;
  persistedId: string | null;
  index: number;
  onPatch: (patch: Partial<RussiaPositionFields>) => void;
  onRemove: () => void;
  onAddPhoto: (file: File) => Promise<{ objectKey: string } | null>;
  onRemovePhoto: (key: string) => void;
}

export function RussiaPositionEditRow({
  row,
  persistedId,
  index,
  onPatch,
  onRemove,
  onAddPhoto,
  onRemovePhoto,
}: RussiaPositionEditRowProps) {
  const { t } = useTranslation(['russiaRequests', 'requests', 'common']);
  const { registerAuthorizer, requestKey } = useDraftPhotoPreviews();

  // Resolve saved-photo signed-download URLs through the shared registry.
  //   Russia item ids are UUIDs, so DraftItemPhotos' own `it-NNNN` guard
  //   stays inert here — the row owns the authorizer registration.
  useEffect(() => {
    if (persistedId === null || row.serverId === null) return;
    for (const key of row.photos) {
      if (ISSUED_PHOTO_KEY_RE.test(key)) {
        registerAuthorizer(key, persistedId, row.serverId);
        requestKey(key);
      }
    }
  }, [row.photos, row.serverId, persistedId, registerAuthorizer, requestKey]);

  return (
    <div
      data-testid={`russia-position-row-${index}`}
      style={{
        border: '1px solid var(--border-1)',
        borderRadius: 8,
        background: 'var(--bg-1)',
        padding: 12,
        display: 'grid',
        gap: 12,
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="package" size={16} style={{ color: 'var(--fg-3)' }} />
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-2)' }}>
          {row.name.trim() || t('russiaRequests:editor.positions.newPosition')}
        </div>
        <div style={{ flex: 1 }} />
        <IconButton
          icon="trash-2"
          size={24}
          label={t('common:actions.delete')}
          onClick={onRemove}
          data-testid={`russia-position-remove-${index}`}
        />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <Field label={t('russiaRequests:editor.positions.fields.name')}>
          <Input
            value={row.name}
            onChange={(v) => onPatch({ name: v })}
            ariaLabel={t('russiaRequests:editor.positions.fields.name')}
            placeholder={t('russiaRequests:editor.positions.fields.name')}
          />
        </Field>
        <Field label={t('russiaRequests:editor.positions.fields.brand')}>
          <Input
            value={row.brand}
            onChange={(v) => onPatch({ brand: v })}
            ariaLabel={t('russiaRequests:editor.positions.fields.brand')}
            placeholder={t('common:fields.optional')}
          />
        </Field>
        <Field label={t('russiaRequests:editor.positions.fields.material')}>
          <Input
            value={row.material}
            onChange={(v) => onPatch({ material: v })}
            ariaLabel={t('russiaRequests:editor.positions.fields.material')}
            placeholder={t('common:fields.optional')}
          />
        </Field>
      </div>

      <Field label={t('russiaRequests:editor.positions.fields.description')}>
        <Textarea
          rows={3}
          value={row.description}
          onChange={(v) => onPatch({ description: v })}
          ariaLabel={t('russiaRequests:editor.positions.fields.description')}
          placeholder={t('russiaRequests:editor.positions.fields.description')}
        />
      </Field>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: 12,
        }}
      >
        <Field label={t('russiaRequests:editor.positions.fields.weightKg')}>
          <Input
            monospace
            value={numToText(row.weightKg)}
            onChange={(v) => onPatch({ weightKg: parseDecimalOrNull(v) })}
            ariaLabel={t('russiaRequests:editor.positions.fields.weightKg')}
            placeholder="0"
          />
        </Field>
        <Field label={t('russiaRequests:editor.positions.fields.volumeM3')}>
          <Input
            monospace
            value={numToText(row.volumeM3)}
            onChange={(v) => onPatch({ volumeM3: parseDecimalOrNull(v) })}
            ariaLabel={t('russiaRequests:editor.positions.fields.volumeM3')}
            placeholder="0"
          />
        </Field>
        <Field label={t('russiaRequests:editor.positions.fields.quantity')}>
          <Input
            monospace
            value={String(row.quantity)}
            onChange={(v) => {
              const n = parseIntOrNull(v);
              if (n !== null) onPatch({ quantity: n });
            }}
            ariaLabel={t('russiaRequests:editor.positions.fields.quantity')}
            placeholder="1"
          />
        </Field>
        <Field label={t('russiaRequests:editor.positions.fields.productQuantity')}>
          <Input
            monospace
            value={numToText(row.productQuantity)}
            onChange={(v) => onPatch({ productQuantity: parseIntOrNull(v) })}
            ariaLabel={t('russiaRequests:editor.positions.fields.productQuantity')}
            placeholder={t('common:fields.optional')}
          />
        </Field>
      </div>

      <Field label={t('russiaRequests:editor.positions.fields.photos')}>
        <DraftItemPhotos
          itemId={row.serverId ?? row.localId}
          photos={row.photos}
          persistedRequestId={persistedId}
          uploading={row.uploading}
          uploadError={row.uploadError}
          frozen={false}
          onAddPhoto={onAddPhoto}
          onRemovePhoto={onRemovePhoto}
        />
      </Field>
    </div>
  );
}

export interface RussiaAddPositionButtonProps {
  onClick: () => void;
}

export function RussiaAddPositionButton({ onClick }: RussiaAddPositionButtonProps) {
  const { t } = useTranslation('russiaRequests');
  return (
    <Button
      type="button"
      kind="secondary"
      icon="plus"
      onClick={onClick}
      data-testid="russia-position-add"
    >
      {t('editor.positions.add')}
    </Button>
  );
}
